import {
  type AwsConnector,
  type GcpConnector,
  LAMBDA_IMAGE,
  LAMBDA_SETTINGS,
  LOG_RETENTION_DAYS,
  POLICY_NAMES,
  renderKmsKeyComment,
  toConnectorDescription,
  verifyCrossAccount,
} from "./iac.ts";

/** The message the script fails with for a secret in another account, until
 * the customer sets the customer-managed key that encrypts it. */
const CROSS_ACCOUNT_KMS_KEY_REQUIRED =
  "The private key's secret is in another AWS account, so it must be encrypted with a customer-managed KMS key. Set KMS_KEY_ARN to that key's ARN, and allow the connector's role in the secret's resource policy and in the key's policy.";

/** An IAM policy document with `statements`. */
const toPolicyDocument = (statements: object[]) => ({
  Version: "2012-10-17",
  Statement: statements,
});

/** Assigns `value`, as JSON, to the shell variable `name`. The heredoc expands
 * the script's own `$` variables in it, and nothing else in JSON is special to
 * it. */
const renderJsonVariable = (name: string, value: object) =>
  `${name}=$(cat <<EOF
${JSON.stringify(value, undefined, 2)}
EOF
)`;

const LAMBDA_TRUST_POLICY = toPolicyDocument([
  {
    Effect: "Allow",
    Principal: { Service: "lambda.amazonaws.com" },
    Action: "sts:AssumeRole",
  },
]);

// The connector writes its own logs, and no others.
const LOGS_POLICY = toPolicyDocument([
  {
    Effect: "Allow",
    Action: ["logs:CreateLogStream", "logs:PutLogEvents"],
    Resource:
      "arn:aws:logs:$CONNECTOR_REGION:$ACCOUNT_ID:log-group:$LOG_GROUP:*",
  },
]);

// "??????" matches the six random characters that end the secret's ARN, so
// the connector gets this one secret and no other.
const READ_PRIVATE_KEY = {
  Sid: "ReadPrivateKey",
  Effect: "Allow",
  Action: "secretsmanager:GetSecretValue",
  Resource: "$PRIVATE_KEY_SECRET_PARTIAL_ARN-??????",
};

const DECRYPT_PRIVATE_KEY = {
  Sid: "DecryptPrivateKey",
  Effect: "Allow",
  Action: "kms:Decrypt",
  Resource: "$KMS_KEY_ARN",
  Condition: {
    StringEquals: {
      "kms:ViaService": "secretsmanager.$SECRETS_REGION.amazonaws.com",
    },
  },
};

const INVOKE_POLICY = toPolicyDocument([
  {
    Effect: "Allow",
    Action: "lambda:InvokeFunction",
    Resource: "$FUNCTION_ARN",
  },
]);

// The tag the script marks what it creates with, whose value is the
// connector's organization.
const OWNER_TAG = "P0GitHubConnector";

/** The message either script fails with for an existing resource, named by
 * `$1`, that it didn't create. */
const NOT_OWNED =
  "The $1 already exists, but this script didn't create it, so it may belong to something else, and the script won't change it. If it's this connector, deployed with the Terraform, update it with the Terraform.";

// How many times, five seconds apart, the script tries to create the function
// while Lambda can't yet assume its new role.
const CREATE_ATTEMPTS = 12;

/** Fails the script for a secret in another account until its KMS key is
 * set. */
const renderCrossAccountCheck = (connector: AwsConnector) =>
  verifyCrossAccount(connector)
    ? `
[[ -n $KMS_KEY_ARN ]] ||
  fail "${CROSS_ACCOUNT_KMS_KEY_REQUIRED}"
`
    : "";

/**
 * Renders the bash script that deploys one GitHub organization's connector,
 * for the customer to run in their own cloud in place of its Terraform (see
 * `renderAwsTerraform`).
 *
 * It deploys what the Terraform does, with the AWS CLI and Docker, and checks
 * what the Terraform's plan checks: the account it's signed in to, and the
 * KMS key that a secret in another account needs. It creates what's missing
 * and sets the rest as the Terraform would, so running it again repairs the
 * connector, or updates it to a new image. It runs on bash 3.2, which macOS
 * ships.
 *
 * Where the Terraform would fail to create a resource whose name is taken,
 * the script tags what it creates with the org, and refuses to change an
 * existing resource without that tag, before it changes anything.
 */
export const renderAwsShell = (connector: AwsConnector): string => {
  const {
    accountId,
    connectorName,
    connectorRegion,
    imageDigest,
    imageTag,
    org,
    p0RoleName,
    secret,
    secretsRegion,
  } = connector;
  const description = toConnectorDescription(org);

  return `#!/usr/bin/env bash
# P0's GitHub connector for ${org}, on AWS Lambda.
#
# This deploys what the connector's Terraform deploys, with the AWS CLI. Save it
# as a file and run it with bash, where Docker and the AWS CLI are installed and
# the AWS CLI is signed in to account ${accountId}. It copies P0's connector
# image from Docker Hub into an ECR repository, which Lambda requires.
#
# P0 runs one connector for each organization, so deploy it once, with this
# script or with the Terraform. Run this again to repair the connector, or to
# update it after changing the image below: it creates what's missing and sets
# the rest as below. It changes only what it created, which it tags, so it
# won't take over anything else that has the connector's name. Each
# organization needs its own connector name: the connector's role, repository
# and log group are named after it, and these names are unique in an AWS
# account.

set -euo pipefail
# The AWS CLI would otherwise page long output, and wait for input.
export AWS_PAGER=""

# The GitHub organization the connector serves, which tags what the script
# creates.
GITHUB_ORG="${org}"
# Where P0 invokes the connector, as entered in P0's installer. P0 calls the
# connector exactly here, so don't change these: to change one, remove the
# organization in P0 and add it again.
ACCOUNT_ID="${accountId}"
CONNECTOR_NAME="${connectorName}"
CONNECTOR_REGION="${connectorRegion}"
# The secret that holds the GitHub App's private key, by its partial ARN: its
# ARN without the hyphen and six random characters that end it. P0 gives the
# connector this ARN. The connector can read this secret and no other.
PRIVATE_KEY_SECRET_PARTIAL_ARN="${secret.partialArn}"
SECRETS_REGION="${secretsRegion}"
${renderKmsKeyComment(connector)}
KMS_KEY_ARN=""
# How many days CloudWatch keeps the connector's logs.
LOG_RETENTION_DAYS=${LOG_RETENTION_DAYS}
# The role P0 assumes in this account, which invokes the connector. Don't
# change it.
P0_ROLE_NAME="${p0RoleName}"
# P0's connector image: its tag on Docker Hub, and the digest that tag resolves
# to. The script copies the image by its digest.
IMAGE_TAG="${imageTag}"
IMAGE_DIGEST="${imageDigest}"

fail() {
  echo "$*" >&2
  exit 1
}

for tool in aws docker; do
  command -v "$tool" >/dev/null || fail "Install $tool, then run this again."
done
# The image is copied with Docker, after the script has started changing
# things, so a stopped Docker would leave the connector half deployed.
docker info >/dev/null 2>&1 ||
  fail "Docker isn't running. Start it, then run this again."

# P0 derives the connector's ARN from the account, region and name above, so
# the connector must be deployed exactly there.
CALLER_ACCOUNT_ID=$(aws sts get-caller-identity \\
  --query Account \\
  --output text \\
  --region "$CONNECTOR_REGION")
[[ $CALLER_ACCOUNT_ID == "$ACCOUNT_ID" ]] ||
  fail "The AWS CLI is signed in to account $CALLER_ACCOUNT_ID. Sign it in to account $ACCOUNT_ID, then run this again."

# The key's ARN goes into the connector's policy as it is.
[[ -z $KMS_KEY_ARN || $KMS_KEY_ARN =~ ^arn:aws:kms:[a-z0-9-]+:[0-9]{12}:key/[A-Za-z0-9-]+$ ]] ||
  fail "Set KMS_KEY_ARN to a KMS key's ARN, as in arn:aws:kms:<region>:<account>:key/<key-id>."
${renderCrossAccountCheck(connector)}
REGISTRY="$ACCOUNT_ID.dkr.ecr.$CONNECTOR_REGION.amazonaws.com"
LOG_GROUP="/aws/lambda/$CONNECTOR_NAME"
ROLE_ARN="arn:aws:iam::$ACCOUNT_ID:role/$CONNECTOR_NAME"
FUNCTION_ARN="arn:aws:lambda:$CONNECTOR_REGION:$ACCOUNT_ID:function:$CONNECTOR_NAME"
OWNER_TAG="${OWNER_TAG}"

# Fails unless $2, the owner tag on the existing $1, is this connector's
# organization.
require_owned() {
  [[ $2 == "$GITHUB_ORG" ]] ||
    fail "${NOT_OWNED}"
}

# Whether the lookup "$@", after the error code $1 that it fails with when the
# resource is missing, finds it. Any other failure, such as a denied or
# throttled lookup, fails the script, so it never reads as missing.
exists() {
  local missing=$1 error
  shift
  if error=$("$@" 2>&1 >/dev/null); then return 0; fi
  [[ $error == *"($missing)"* ]] || fail "$error"
  return 1
}

# P0's AWS IAM management creates P0's role. The script lets that role invoke
# the connector last, so it checks the role is there before changing anything.
exists NoSuchEntity aws iam get-role --role-name "$P0_ROLE_NAME" ||
  fail "The role $P0_ROLE_NAME isn't in account $ACCOUNT_ID. Install P0's AWS IAM management for this account, then run this again."

echo "Checking what already has the name $CONNECTOR_NAME"
# Each check comes before any change, so finding something the script didn't
# create changes nothing.
REPOSITORY_EXISTS=""
if exists RepositoryNotFoundException aws ecr describe-repositories \\
  --repository-names "$CONNECTOR_NAME" \\
  --region "$CONNECTOR_REGION"; then
  REPOSITORY_EXISTS=1
  OWNER=$(aws ecr list-tags-for-resource \\
    --resource-arn "arn:aws:ecr:$CONNECTOR_REGION:$ACCOUNT_ID:repository/$CONNECTOR_NAME" \\
    --query "tags[?Key=='$OWNER_TAG'].Value | [0]" \\
    --output text \\
    --region "$CONNECTOR_REGION")
  require_owned "ECR repository $CONNECTOR_NAME" "$OWNER"
fi
ROLE_EXISTS=""
if exists NoSuchEntity aws iam get-role --role-name "$CONNECTOR_NAME"; then
  ROLE_EXISTS=1
  OWNER=$(aws iam list-role-tags \\
    --role-name "$CONNECTOR_NAME" \\
    --query "Tags[?Key=='$OWNER_TAG'].Value | [0]" \\
    --output text)
  require_owned "role $CONNECTOR_NAME" "$OWNER"
fi
LOG_GROUP_EXISTS=""
# Lists no log group when it's missing, rather than failing.
LOG_GROUPS=$(aws logs describe-log-groups \\
  --log-group-name-prefix "$LOG_GROUP" \\
  --query "logGroups[?logGroupName=='$LOG_GROUP'].logGroupName" \\
  --output text \\
  --region "$CONNECTOR_REGION")
if [[ -n $LOG_GROUPS ]]; then
  LOG_GROUP_EXISTS=1
  OWNER=$(aws logs list-tags-for-resource \\
    --resource-arn "arn:aws:logs:$CONNECTOR_REGION:$ACCOUNT_ID:log-group:$LOG_GROUP" \\
    --query "tags.$OWNER_TAG" \\
    --output text \\
    --region "$CONNECTOR_REGION")
  require_owned "log group $LOG_GROUP" "$OWNER"
fi
FUNCTION_EXISTS=""
if exists ResourceNotFoundException aws lambda get-function \\
  --function-name "$CONNECTOR_NAME" \\
  --region "$CONNECTOR_REGION"; then
  FUNCTION_EXISTS=1
  OWNER=$(aws lambda list-tags \\
    --resource "$FUNCTION_ARN" \\
    --query "Tags.$OWNER_TAG" \\
    --output text \\
    --region "$CONNECTOR_REGION")
  require_owned "Lambda function $CONNECTOR_NAME" "$OWNER"
fi

echo "Setting up the ECR repository $CONNECTOR_NAME"
# Tags can't be overwritten, so no one who can push to this repository can
# replace the image that runs with access to the key.
if [[ -n $REPOSITORY_EXISTS ]]; then
  aws ecr put-image-tag-mutability \\
    --repository-name "$CONNECTOR_NAME" \\
    --image-tag-mutability IMMUTABLE \\
    --region "$CONNECTOR_REGION" >/dev/null
  aws ecr put-image-scanning-configuration \\
    --repository-name "$CONNECTOR_NAME" \\
    --image-scanning-configuration scanOnPush=true \\
    --region "$CONNECTOR_REGION" >/dev/null
else
  aws ecr create-repository \\
    --repository-name "$CONNECTOR_NAME" \\
    --image-tag-mutability IMMUTABLE \\
    --image-scanning-configuration scanOnPush=true \\
    --tags "Key=$OWNER_TAG,Value=$GITHUB_ORG" \\
    --region "$CONNECTOR_REGION" >/dev/null
fi

echo "Setting up the role $CONNECTOR_NAME"
${renderJsonVariable("LAMBDA_TRUST_POLICY", LAMBDA_TRUST_POLICY)}
if [[ -n $ROLE_EXISTS ]]; then
  aws iam update-assume-role-policy \\
    --role-name "$CONNECTOR_NAME" \\
    --policy-document "$LAMBDA_TRUST_POLICY"
else
  aws iam create-role \\
    --role-name "$CONNECTOR_NAME" \\
    --assume-role-policy-document "$LAMBDA_TRUST_POLICY" \\
    --tags "Key=$OWNER_TAG,Value=$GITHUB_ORG" >/dev/null
fi

echo "Setting up the log group $LOG_GROUP"
# The connector's role can't create a log group, so its own must exist before
# it runs.
if [[ -z $LOG_GROUP_EXISTS ]]; then
  aws logs create-log-group \\
    --log-group-name "$LOG_GROUP" \\
    --tags "$OWNER_TAG=$GITHUB_ORG" \\
    --region "$CONNECTOR_REGION"
fi
aws logs put-retention-policy \\
  --log-group-name "$LOG_GROUP" \\
  --retention-in-days "$LOG_RETENTION_DAYS" \\
  --region "$CONNECTOR_REGION"

${renderJsonVariable("LOGS_POLICY", LOGS_POLICY)}
aws iam put-role-policy \\
  --role-name "$CONNECTOR_NAME" \\
  --policy-name ${POLICY_NAMES.logs} \\
  --policy-document "$LOGS_POLICY"

# The connector reads the GitHub App's private key, and nothing else.
if [[ -z $KMS_KEY_ARN ]]; then
  ${renderJsonVariable("PRIVATE_KEY_POLICY", toPolicyDocument([READ_PRIVATE_KEY]))}
else
  ${renderJsonVariable(
    "PRIVATE_KEY_POLICY",
    toPolicyDocument([READ_PRIVATE_KEY, DECRYPT_PRIVATE_KEY])
  )}
fi
aws iam put-role-policy \\
  --role-name "$CONNECTOR_NAME" \\
  --policy-name ${POLICY_NAMES.privateKey} \\
  --policy-document "$PRIVATE_KEY_POLICY"

echo "Copying P0's connector image into ECR"
# ECR's tags can't be overwritten, so each copy is tagged with the image's tag
# and the time it was copied: copying the image again, to roll back or after an
# interrupted run, pushes a new tag.
COPY_TAG="$IMAGE_TAG-$(date -u +%Y%m%d%H%M%S)"
SOURCE="${LAMBDA_IMAGE}@$IMAGE_DIGEST"
TARGET="$REGISTRY/$CONNECTOR_NAME:$COPY_TAG"
(
  # Signs Docker out of ECR at the end, so its login doesn't outlast the copy.
  # A failed sign-out doesn't fail the copy once the image is pushed.
  trap 'docker logout "$REGISTRY" || true' EXIT
  aws ecr get-login-password --region "$CONNECTOR_REGION" |
    docker login --username AWS --password-stdin "$REGISTRY"
  docker pull --platform linux/amd64 "$SOURCE"
  docker tag "$SOURCE" "$TARGET"
  docker push "$TARGET"
)
# Pulling one platform and pushing it gives ECR that image's own manifest, not
# the tag's multi-platform one, so the digest there can differ from the one
# pinned above. Lambda needs ECR's.
ECR_DIGEST=$(aws ecr describe-images \\
  --repository-name "$CONNECTOR_NAME" \\
  --image-ids imageTag="$COPY_TAG" \\
  --query "imageDetails[0].imageDigest" \\
  --output text \\
  --region "$CONNECTOR_REGION")
IMAGE_URI="$REGISTRY/$CONNECTOR_NAME@$ECR_DIGEST"

echo "Deploying the Lambda function $CONNECTOR_NAME"
# No VPC: the connector calls api.github.com over the internet. Updating
# detaches any VPC the function was given since.
if [[ -n $FUNCTION_EXISTS ]]; then
  aws lambda update-function-configuration \\
    --function-name "$CONNECTOR_NAME" \\
    --description "${description}" \\
    --role "$ROLE_ARN" \\
    --vpc-config "SubnetIds=[],SecurityGroupIds=[]" \\
    --memory-size ${LAMBDA_SETTINGS.memorySize} \\
    --timeout ${LAMBDA_SETTINGS.timeout} \\
    --region "$CONNECTOR_REGION" >/dev/null
  aws lambda wait function-updated \\
    --function-name "$CONNECTOR_NAME" \\
    --region "$CONNECTOR_REGION"
  aws lambda update-function-code \\
    --function-name "$CONNECTOR_NAME" \\
    --image-uri "$IMAGE_URI" \\
    --architectures ${LAMBDA_SETTINGS.architecture} \\
    --region "$CONNECTOR_REGION" >/dev/null
  aws lambda wait function-updated \\
    --function-name "$CONNECTOR_NAME" \\
    --region "$CONNECTOR_REGION"
else
  # Lambda can't assume a new role for the first few seconds.
  for attempt in {1..${CREATE_ATTEMPTS}}; do
    if ERROR=$(aws lambda create-function \\
      --function-name "$CONNECTOR_NAME" \\
      --description "${description}" \\
      --role "$ROLE_ARN" \\
      --package-type Image \\
      --code ImageUri="$IMAGE_URI" \\
      --architectures ${LAMBDA_SETTINGS.architecture} \\
      --memory-size ${LAMBDA_SETTINGS.memorySize} \\
      --timeout ${LAMBDA_SETTINGS.timeout} \\
      --tags "$OWNER_TAG=$GITHUB_ORG" \\
      --region "$CONNECTOR_REGION" 2>&1 >/dev/null); then
      break
    fi
    [[ $ERROR == *"cannot be assumed by Lambda"* && $attempt -lt ${CREATE_ATTEMPTS} ]] ||
      fail "$ERROR"
    sleep 5
  done
  aws lambda wait function-active \\
    --function-name "$CONNECTOR_NAME" \\
    --region "$CONNECTOR_REGION"
fi

echo "Letting P0 invoke the connector"
# P0 invokes the connector as its role in this account, and needs nothing else
# on it.
${renderJsonVariable("INVOKE_POLICY", INVOKE_POLICY)}
aws iam put-role-policy \\
  --role-name "$P0_ROLE_NAME" \\
  --policy-name "$CONNECTOR_NAME-invoke" \\
  --policy-document "$INVOKE_POLICY"

echo "Deployed the connector $CONNECTOR_NAME in account $ACCOUNT_ID, region $CONNECTOR_REGION."
`;
};

// The label the script marks the Cloud Run service it creates with, whose
// value is the connector's organization. A label's value is lowercase.
const GCP_OWNER_LABEL = "p0_github_connector";

// How many times, five seconds apart, the script tries to grant a new service
// account read on the secret while IAM doesn't know it yet.
const GRANT_ATTEMPTS = 12;

/**
 * Renders the bash script that deploys one GitHub organization's connector on
 * Cloud Run, for the customer to run in their own cloud in place of its
 * Terraform (see `renderGcpTerraform`).
 *
 * It deploys what the Terraform does, with the gcloud CLI. It creates what's
 * missing and sets the rest as the Terraform would, so running it again
 * repairs the connector, or updates it to a new image. It runs on bash 3.2,
 * which macOS ships.
 *
 * Where the Terraform would fail to create a resource whose name is taken,
 * the script marks what it creates with the org, a label on the service and
 * the description of the service account, and refuses to change an existing
 * one without that mark, before it changes anything.
 */
export const renderGcpShell = (connector: GcpConnector): string => {
  const {
    connectorName,
    connectorRegion,
    image,
    imageTag,
    org,
    p0ServiceAccountEmail,
    projectId,
    secret,
    serviceAccountId,
  } = connector;
  const description = toConnectorDescription(org);

  return `#!/usr/bin/env bash
# P0's GitHub connector for ${org}, on Google Cloud Run.
#
# This deploys what the connector's Terraform deploys, with the gcloud CLI. Save
# it as a file and run it with bash, where the gcloud CLI is installed and signed
# in as someone who can enable APIs (Service Usage) and manage Cloud Run, service
# accounts and IAM in project ${projectId}, and IAM on the private key's secret in
# project ${secret.projectId}.
#
# P0 runs one connector for each organization, so deploy it once, with this
# script or with the Terraform. Run this again to repair the connector, or to
# update it after changing the image below: it creates what's missing and sets
# the rest as below. It changes only what it created, which it marks, so it
# won't take over anything else that has the connector's name. Each
# organization needs its own connector name: the connector's service and
# service account are named after it, and these names are unique in a Google
# Cloud project.

set -euo pipefail

# The GitHub organization the connector serves, which marks what the script
# creates.
GITHUB_ORG="${org}"
OWNER="${org.toLowerCase()}"
# Where P0 invokes the connector, as entered in P0's installer. P0 calls the
# connector exactly here, so don't change these: to change one, remove the
# organization in P0 and add it again.
PROJECT_ID="${projectId}"
CONNECTOR_NAME="${connectorName}"
CONNECTOR_REGION="${connectorRegion}"
# The secret that holds the GitHub App's private key, by its name in its
# project. P0 gives the connector its resource name,
# ${secret.resourceName}.
# The connector can read this secret and no other.
PRIVATE_KEY_SECRET_ID="${secret.name}"
SECRETS_PROJECT_ID="${secret.projectId}"
# The connector's own service account, named after the connector.
SERVICE_ACCOUNT_ID="${serviceAccountId}"
# P0's service account, which invokes the connector.
P0_SERVICE_ACCOUNT_EMAIL="${p0ServiceAccountEmail}"
# P0's connector image (${imageTag}), pinned by its digest.
IMAGE="${image}"

fail() {
  echo "$*" >&2
  exit 1
}

command -v gcloud >/dev/null || fail "Install gcloud, then run this again."

ACCOUNT=$(gcloud config get-value account 2>/dev/null)
[[ -n $ACCOUNT ]] ||
  fail "The gcloud CLI isn't signed in. Sign it in with gcloud auth login, then run this again."

SERVICE_ACCOUNT_EMAIL="$SERVICE_ACCOUNT_ID@$PROJECT_ID.iam.gserviceaccount.com"
DESCRIPTION="${description}"
OWNER_LABEL="${GCP_OWNER_LABEL}"

# Fails unless $2, the mark on the existing $1, is this connector's.
require_owned() {
  [[ $2 == "$3" ]] ||
    fail "${NOT_OWNED}"
}

echo "Enabling the APIs the connector needs in project $PROJECT_ID"
gcloud services enable \\
  iam.googleapis.com \\
  run.googleapis.com \\
  secretmanager.googleapis.com \\
  --project "$PROJECT_ID"

echo "Checking what already has the name $CONNECTOR_NAME"
# Each check comes before any other change, so finding something the script
# didn't create changes nothing. A lookup lists nothing for what's missing, and
# fails the script on any other failure, such as a denied lookup. gcloud's
# filters can match on words, so only the row with this exact name is kept.
exact_row() {
  awk -F '\\t' -v name="$1" '$1 == name'
}
SERVICE_ACCOUNT=$(gcloud iam service-accounts list \\
  --project "$PROJECT_ID" \\
  --filter "email=$SERVICE_ACCOUNT_EMAIL" \\
  --format "value(email,description)" | exact_row "$SERVICE_ACCOUNT_EMAIL")
if [[ -n $SERVICE_ACCOUNT ]]; then
  require_owned "service account $SERVICE_ACCOUNT_EMAIL" \\
    "$(cut -s -f2 <<<"$SERVICE_ACCOUNT")" "$DESCRIPTION"
fi
SERVICE=$(gcloud run services list \\
  --project "$PROJECT_ID" \\
  --region "$CONNECTOR_REGION" \\
  --filter "metadata.name=$CONNECTOR_NAME" \\
  --format "value(metadata.name,metadata.labels.$OWNER_LABEL)" |
  exact_row "$CONNECTOR_NAME")
if [[ -n $SERVICE ]]; then
  require_owned "Cloud Run service $CONNECTOR_NAME" \\
    "$(cut -s -f2 <<<"$SERVICE")" "$OWNER"
fi

echo "Setting up the service account $SERVICE_ACCOUNT_EMAIL"
if [[ -z $SERVICE_ACCOUNT ]]; then
  gcloud iam service-accounts create "$SERVICE_ACCOUNT_ID" \\
    --display-name "$DESCRIPTION" \\
    --description "$DESCRIPTION" \\
    --project "$PROJECT_ID"
fi

echo "Letting the connector read the private key's secret"
# The connector reads the private key's value (secretmanager.versions.access),
# then its version (secretmanager.versions.get). The Secret Accessor role has
# only the first, so the Viewer role is granted too, on this one secret. IAM
# may not know a new service account for the first few seconds.
for role in roles/secretmanager.secretAccessor roles/secretmanager.viewer; do
  for attempt in {1..${GRANT_ATTEMPTS}}; do
    if ERROR=$(gcloud secrets add-iam-policy-binding "$PRIVATE_KEY_SECRET_ID" \\
      --member "serviceAccount:$SERVICE_ACCOUNT_EMAIL" \\
      --role "$role" \\
      --condition None \\
      --project "$SECRETS_PROJECT_ID" 2>&1 >/dev/null); then
      break
    fi
    [[ $ERROR == *"does not exist"* && $attempt -lt ${GRANT_ATTEMPTS} ]] ||
      fail "$ERROR"
    sleep 5
  done
done

echo "Deploying the Cloud Run service $CONNECTOR_NAME"
# No VPC, by connector or by Direct VPC egress: the connector calls
# api.github.com over the internet. P0 calls it from outside Google Cloud, so
# IAM, not the network, decides who can invoke it, and the connector checks
# each caller's identity token against INVOKER_SA_EMAIL too.
gcloud run deploy "$CONNECTOR_NAME" \\
  --image "$IMAGE" \\
  --service-account "$SERVICE_ACCOUNT_EMAIL" \\
  --set-env-vars "INVOKER_SA_EMAIL=$P0_SERVICE_ACCOUNT_EMAIL" \\
  --description "$DESCRIPTION" \\
  --update-labels "$OWNER_LABEL=$OWNER" \\
  --ingress all \\
  --no-allow-unauthenticated \\
  --clear-vpc-connector \\
  --clear-network \\
  --region "$CONNECTOR_REGION" \\
  --project "$PROJECT_ID" \\
  --quiet

echo "Letting P0 invoke the connector"
# P0 invokes the connector as its service account, and reads the service to
# find its URL when it installs. It needs nothing else on it.
for role in roles/run.invoker roles/run.viewer; do
  gcloud run services add-iam-policy-binding "$CONNECTOR_NAME" \\
    --member "serviceAccount:$P0_SERVICE_ACCOUNT_EMAIL" \\
    --role "$role" \\
    --region "$CONNECTOR_REGION" \\
    --project "$PROJECT_ID" >/dev/null
done

echo "Deployed the connector $CONNECTOR_NAME in project $PROJECT_ID, region $CONNECTOR_REGION."
`;
};
